import { Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { functions } from '@/src/lib/functions';

import {
  CURRENT_PRIVACY_CONTENT_HASH,
  CURRENT_PRIVACY_DOCUMENT_ID,
  CURRENT_PRIVACY_VERSION,
  CURRENT_TERMS_CONTENT_HASH,
  CURRENT_TERMS_DOCUMENT_ID,
  CURRENT_TERMS_VERSION,
} from './legal-documents';

export type LegalAcceptanceSource = 'registration' | 'update';

export type LegalAcceptance = {
  termsVersion: string;
  privacyVersion: string;
  termsDocumentId: string;
  privacyDocumentId: string;
  termsContentHash: string;
  privacyContentHash: string;
  acceptedAt: Timestamp | null;
};

export type LegalAcceptanceRecord = LegalAcceptance & {
  uid: string;
  source: LegalAcceptanceSource;
};

type AcceptCurrentLegalResponse = {
  legal: {
    acceptedAtMillis?: number;
    privacyContentHash: string;
    privacyDocumentId: string;
    privacyVersion: string;
    termsContentHash: string;
    termsDocumentId: string;
    termsVersion: string;
  };
  ok: true;
};

const acceptCurrentLegalCallable = httpsCallable<
  { source: LegalAcceptanceSource },
  AcceptCurrentLegalResponse
>(functions, 'acceptCurrentLegal');

export function hasRecordedLegalAcceptance(
  legal: LegalAcceptance | null | undefined,
) {
  return Boolean(
    legal?.termsVersion &&
    legal.privacyVersion &&
    legal.termsDocumentId &&
    legal.privacyDocumentId &&
    legal.termsContentHash &&
    legal.privacyContentHash &&
    legal.acceptedAt,
  );
}

export function hasAcceptedCurrentLegal(
  legal: LegalAcceptance | null | undefined,
) {
  return (
    legal?.termsVersion === CURRENT_TERMS_VERSION &&
    legal?.privacyVersion === CURRENT_PRIVACY_VERSION &&
    legal?.termsDocumentId === CURRENT_TERMS_DOCUMENT_ID &&
    legal?.privacyDocumentId === CURRENT_PRIVACY_DOCUMENT_ID &&
    legal?.termsContentHash === CURRENT_TERMS_CONTENT_HASH &&
    legal?.privacyContentHash === CURRENT_PRIVACY_CONTENT_HASH &&
    Boolean(legal.acceptedAt)
  );
}

export async function acceptCurrentLegal({
  source,
}: {
  uid: string;
  email: string | null;
  source: LegalAcceptanceSource;
}) {
  const result = await acceptCurrentLegalCallable({ source });
  const legal = result.data.legal;
  const acceptedAtMillis =
    typeof legal.acceptedAtMillis === 'number'
      ? legal.acceptedAtMillis
      : Date.now();

  return {
    termsVersion: legal.termsVersion,
    privacyVersion: legal.privacyVersion,
    termsDocumentId: legal.termsDocumentId,
    privacyDocumentId: legal.privacyDocumentId,
    termsContentHash: legal.termsContentHash,
    privacyContentHash: legal.privacyContentHash,
    acceptedAt: Timestamp.fromMillis(acceptedAtMillis),
  };
}
